import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { withAuthenticationRequired } from '@auth0/auth0-react';

import { ApplicationLayout } from 'shared/components/ApplicationLayout';
import { UserRole } from 'shared/models';
import {
  selectRole,
  selectIsStreamlinedProspect,
  selectAccessTokenExpirationDate
} from 'shared/store/modules/auth/selectors';

import { useAuth0Session } from 'shared/store/modules/auth/hooks';
import Loading from '../Loading';
import { useAccounts } from 'shared/store/modules/accounts/hooks';

const AdminSession = withAuthenticationRequired(
  () => {
    const userRole = useSelector(selectRole);
    const accessTokenExpiration = useSelector(selectAccessTokenExpirationDate);
    // CS-5802: Get isRenewing from hook (local state), NOT Redux selector
    // The hook's renewSession() only updates local state, not Redux
    const { renewSession, isRenewing } = useAuth0Session();

    // CS-5802: Check if token is expired - must block rendering until refresh completes
    // Default to false (not expired) if no expiration date yet - Auth0 handles actual validation
    const isTokenExpired = accessTokenExpiration ? new Date(accessTokenExpiration) < new Date() : false;

    const needsSessionRefresh = !userRole || isTokenExpired;

    useEffect(() => {
      if (needsSessionRefresh && !isRenewing) {
        renewSession();
      }
    }, [needsSessionRefresh, renewSession, isRenewing]);

    // Block rendering until we have a valid, non-expired token
    if (needsSessionRefresh || isRenewing) return <Loading />;

    return <ApplicationLayout />;
  },
  { returnTo: window.location.pathname }
);

const UserSession = withAuthenticationRequired(
  () => {
    const userRole = useSelector(selectRole);
    const accessTokenExpiration = useSelector(selectAccessTokenExpirationDate);
    const isStreamlinedProspect = useSelector(selectIsStreamlinedProspect);
    const { accounts, activeAccountId } = useAccounts();
    // CS-5802: isRenewing comes from hook's local state (not Redux)
    const { startSession, isRenewing } = useAuth0Session();

    // CS-5802: Check if token is expired - must block rendering until refresh completes
    // Default to false (not expired) if no expiration date yet - Auth0 handles actual validation
    const isTokenExpired = accessTokenExpiration ? new Date(accessTokenExpiration) < new Date() : false;

    const needsSessionRefresh = !userRole || isTokenExpired;

    useEffect(() => {
      if (needsSessionRefresh && !isRenewing) {
        startSession();
      }
      // eslint-disable-next-line
    }, [needsSessionRefresh, isRenewing]);

    if (needsSessionRefresh || isRenewing || accounts.length === 0) return <Loading />;

    if (userRole === UserRole.Prospect) {
      if (isStreamlinedProspect === true) return <Navigate to="/signup/next" replace />;
      return <Navigate to="/residential/next" replace />;
    }

    if (accounts.length > 1 && !activeAccountId) return <Navigate to="/accounts" replace />;

    return <ApplicationLayout />;
  },
  { returnTo: window.location.pathname }
);

export const Session = () => {
  const { error, isAdmin, onError } = useAuth0Session();

  useEffect(() => {
    if (error) {
      console.error(error);
      onError(error);
    }
  }, [error, onError]);

  if (error) return <Loading />;

  if (isAdmin()) return <AdminSession />;
  return <UserSession />;
};
